import React from 'react';
import { TouchableOpacity, Text, StyleSheet, View } from 'react-native';
import { Svg, Path } from 'react-native-svg';

interface FilterChipProps {
  icon?: React.ReactNode;
  label: string;
  active: boolean;
  onPress: () => void;
  onClear?: () => void;
}

export default function FilterChip({ icon, label, active, onPress, onClear }: FilterChipProps) {
  return (
    <TouchableOpacity
      style={[styles.chip, active && styles.activeChip]}
      onPress={onPress}
      activeOpacity={0.8}
    >
      {icon ? <View style={styles.iconSlot}>{icon}</View> : null}
      <Text style={[styles.label, active && styles.activeLabel]} numberOfLines={1}>
        {label}
      </Text>
      {active && onClear ? (
        <TouchableOpacity onPress={onClear} hitSlop={8}>
          <Svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <Path d="M3.16992 8.82843L8.82678 3.17158" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M3.16992 3.17157L8.82678 8.82842" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        </TouchableOpacity>
      ) : (
        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <Path d="M12 6L8 10L4 6" stroke="#3D3C38" strokeWidth="1.5" strokeLinecap="square" />
        </Svg>
      )}
    </TouchableOpacity>
  );
}

export const ClockChipIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M9.82627 9.27716L7.96094 8.1629V5.75537" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M3.18359 4.42266C3.46377 4.04066 3.78871 3.69431 4.15127 3.38818" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M11.7062 12.7241C10.8021 13.4331 9.68587 13.8909 8.45421 13.9831C7.84781 14.0285 7.25567 13.9805 6.69141 13.854" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M4.56763 12.9074C3.66351 12.2822 2.94034 11.4118 2.48828 10.3857" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M2.21907 6.36475C2.04395 7.00683 1.96807 7.68589 2.01996 8.38703" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M13.0969 11.1488C13.7034 10.1701 14.0413 9.0124 13.9939 7.77553C13.8662 4.46066 11.0767 1.87737 7.76172 2.0045" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export const CalendarChipIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M10.1016 2.46143V4.44497" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M5.28516 2.46143V4.44497" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0817 3.37598 10.4566 3.37598Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M5.01562 7.625H5.02229" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M5.01562 9.92773H5.02229" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M7.69141 7.625H7.69807" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M7.69141 9.92773H7.69807" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M10.3594 7.625H10.366" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M10.3594 9.92773H10.366" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export const CategoryChipIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <Path fillRule="evenodd" clipRule="evenodd" d="M3 6.5C3 3.87479 3.02811 3 6.5 3C9.97189 3 10 3.87479 10 6.5C10 9.12521 10.0111 10 6.5 10C2.98893 10 3 9.12521 3 6.5Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path fillRule="evenodd" clipRule="evenodd" d="M14 6.5C14 3.87479 14.0281 3 17.5 3C20.9719 3 21 3.87479 21 6.5C21 9.12521 21.0111 10 17.5 10C13.9889 10 14 9.12521 14 6.5Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path fillRule="evenodd" clipRule="evenodd" d="M3 17.5C3 14.8748 3.02811 14 6.5 14C9.97189 14 10 14.8748 10 17.5C10 20.1252 10.0111 21 6.5 21C2.98893 21 3 20.1252 3 17.5Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path fillRule="evenodd" clipRule="evenodd" d="M14 17.5C14 14.8748 14.0281 14 17.5 14C20.9719 14 21 14.8748 21 17.5C21 20.1252 21.0111 21 17.5 21C13.9889 21 14 20.1252 14 17.5Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F0EEE4',
  },
  activeChip: {
    backgroundColor: '#F0EEE4',
    borderColor: '#151515',
  },
  iconSlot: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#3D3C38',
    lineHeight: 16.8,
  },
  activeLabel: {
    color: '#151515',
  },
});
